import { useCallback } from 'react';
import { SelectedMethodInfo, PriceRangeTier } from '../BulkEditModal';
import { rateHasAnyDiscount } from '@pages/checkout-app/utils/string.utils';
import { stripHtmlTags } from '@utils/string-utils';
import {
  ConflictingMethodInfo,
  DuplicateNameValidationResult,
  NameDescriptionFormDataForValidation,
  EditedMethodInfo,
  UseDuplicateNameValidationProps,
  UseDuplicateNameValidationReturn,
} from './useDuplicateNameValidation.types';

export { ConflictingMethodInfo, DuplicateNameValidationResult, NameDescriptionFormDataForValidation };

const methodHasAnyDiscount = (method: SelectedMethodInfo): boolean => {
  const hasMainDiscount = method.hasDiscount;
  const hasAdditionalPriceRangeDiscounts =
    method.additionalPriceRanges &&
    Array.isArray(method.additionalPriceRanges) &&
    method.additionalPriceRanges.some((item: PriceRangeTier) => item.hasDiscount === true);

  return hasMainDiscount || hasAdditionalPriceRangeDiscounts;
};

export const useDuplicateNameValidation = ({
  selectedMethods,
  allShippingMethods,
}: UseDuplicateNameValidationProps): UseDuplicateNameValidationReturn => {
  const validateNameDescription = useCallback(
    (formData: NameDescriptionFormDataForValidation): DuplicateNameValidationResult => {
      if (!selectedMethods || selectedMethods.length === 0) {
        return { valid: true };
      }

      const editedMethods: EditedMethodInfo[] = selectedMethods.map((method) => {
        const originalName = stripHtmlTags(method.name).trim();
        const newName = formData.name.trim() ? stripHtmlTags(formData.name).trim() : originalName;

        const originalNotes = stripHtmlTags(method.note || '').trim();
        let newNotes = originalNotes;
        if (formData.noteAction === 'remove') {
          newNotes = '';
        } else if (formData.noteAction === 'change' && formData.note.trim()) {
          newNotes = stripHtmlTags(formData.note).trim();
        }

        return {
          id: method.id,
          shippingMethodId: method.shippingMethodId,
          originalName,
          name: newName.toLowerCase(),
          notes: newNotes.toLowerCase(),
          originalNotes,
          hasDiscount: methodHasAnyDiscount(method),
          combination: newName.toLowerCase() + newNotes.toLowerCase(),
          groupName: method.groupName,
        };
      });

      const combinationMap = new Map<string, EditedMethodInfo[]>();
      for (const method of editedMethods) {
        const key = method.combination;
        if (!combinationMap.has(key)) {
          combinationMap.set(key, []);
        }
        combinationMap.get(key)!.push(method);
      }

      for (const [, methods] of combinationMap) {
        if (methods.length > 1 && methods.some((m) => m.hasDiscount)) {
          const conflictingMethods: ConflictingMethodInfo[] = methods.map((m) => ({
            id: m.id,
            name: m.originalName,
            description: m.originalNotes,
            hasDiscount: m.hasDiscount,
            groupName: m.groupName,
          }));

          const methodNames = conflictingMethods.map((m) => `"${m.name}"`).join(' and ');
          const resultingName = methods[0].name;
          const resultingNotes = methods[0].notes;
          const resultingDesc = resultingNotes ? ` with description "${resultingNotes}"` : ' with no description';

          return {
            valid: false,
            error: `Methods ${methodNames} would both have the name "${resultingName}"${resultingDesc}. At least one has a discount, so they must have unique name + description combinations.`,
            conflictingMethods,
          };
        }
      }

      const selectedShippingMethodIds = new Set(
        selectedMethods.map((m) => m.shippingMethodId).filter((id): id is number => id !== undefined),
      );
      const nonEditedMethods = allShippingMethods.filter(
        (m) => !m.shippingMethodId || !selectedShippingMethodIds.has(m.shippingMethodId),
      );

      const processedNonEditedMethods = nonEditedMethods.map((m) => {
        const strippedName = stripHtmlTags(m.name || '').trim();
        const strippedNotes = stripHtmlTags(m.notes || '').trim();
        return {
          original: m,
          strippedName,
          strippedNotes,
          combination: strippedName.toLowerCase() + strippedNotes.toLowerCase(),
          hasDiscount: rateHasAnyDiscount(m),
        };
      });

      for (const editedMethod of editedMethods) {
        const conflictingProcessed = processedNonEditedMethods.find((existing) => {
          if (existing.combination === editedMethod.combination) {
            return editedMethod.hasDiscount || existing.hasDiscount;
          }
          return false;
        });

        if (conflictingProcessed) {
          const existingName = conflictingProcessed.strippedName;
          const existingNotes = conflictingProcessed.strippedNotes;
          const conflictingMethod = conflictingProcessed.original;

          const conflictingMethods: ConflictingMethodInfo[] = [
            {
              id: editedMethod.id,
              name: editedMethod.originalName,
              description: editedMethod.originalNotes,
              hasDiscount: editedMethod.hasDiscount,
              groupName: editedMethod.groupName,
            },
            {
              id: conflictingMethod.id?.toString() || '',
              name: existingName,
              description: existingNotes,
              hasDiscount: conflictingProcessed.hasDiscount,
            },
          ];

          const resultingDesc = editedMethod.notes
            ? ` with description "${editedMethod.notes}"`
            : ' with no description';

          return {
            valid: false,
            error: `Method "${editedMethod.originalName}" would have the same name "${editedMethod.name}"${resultingDesc} as existing method "${existingName}". At least one has a discount, so they must have unique name + description combinations.`,
            conflictingMethods,
          };
        }
      }

      return { valid: true };
    },
    [selectedMethods, allShippingMethods],
  );

  return { validateNameDescription };
};
